import { useMsal } from "@azure/msal-react";
import { getToken, useLogin } from "../../authConfig";
import { Spinner } from "@fluentui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { HistoryData, HistoryItem } from "../HistoryItem";
import { Answers, HistoryProviderOptions } from "../HistoryProviders/IProvider";
import { useHistoryManager, HistoryMetaData } from "../HistoryProviders";
import { useTranslation } from "react-i18next";
import styles from "./HistoryPanel.module.css";

const HISTORY_COUNT_PER_LOAD = 20;

export const HistoryPanel = ({
    provider,
    isOpen,
    notify,
    onClose,
    onChatSelected,
    onNewChat,
    activeSessionId
}: {
    provider: HistoryProviderOptions;
    isOpen: boolean;
    notify: number;
    onClose: () => void;
    onChatSelected: (answers: Answers) => void;
    onNewChat?: () => void;
    activeSessionId?: string | null;
}) => {
    const historyManager = useHistoryManager(provider);
    const [history, setHistory] = useState<HistoryMetaData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const isLoadingRef = useRef(false);

    const client = useLogin ? useMsal().instance : undefined;
    const msalContext = useLogin ? useMsal() : undefined;
    const userName = msalContext?.accounts[0]?.name || msalContext?.accounts[0]?.username?.split('@')[0] || "user";

    useEffect(() => {
        if (!isOpen) return;
        setHistory([]);
        historyManager.resetContinuationToken();
        setHasMoreHistory(true);
    }, [isOpen, notify]);

    // Auto-load when opened
    useEffect(() => {
        if (isOpen && history.length === 0 && hasMoreHistory) {
            loadMoreHistory();
        }
    }, [isOpen, hasMoreHistory]);

    const loadMoreHistory = async () => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        setIsLoading(true);
        const token = client ? await getToken(client) : undefined;
        const items = await historyManager.getNextItems(HISTORY_COUNT_PER_LOAD, token);
        if (items.length === 0) {
            setHasMoreHistory(false);
        }
        setHistory(prevHistory => [...prevHistory, ...items]);
        isLoadingRef.current = false;
        setIsLoading(false);
    };

    const handleSelect = async (id: string) => {
        const token = client ? await getToken(client) : undefined;
        const item = await historyManager.getItem(id, token);
        if (item) {
            onChatSelected(item);
        }
    };

    const handleDelete = async (id: string) => {
        const token = client ? await getToken(client) : undefined;
        await historyManager.deleteItem(id, token);
        setHistory(prevHistory => prevHistory.filter(item => item.id !== id));
        if (id === activeSessionId) {
            if (onNewChat) onNewChat();
            else onChatSelected([]);
        }
    };

    const handleNewConversation = () => {
        if (onNewChat) onNewChat();
        else onChatSelected([]);
    };

    const groupedHistory = useMemo(() => groupHistory(history), [history]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();

    return (
        <aside className={`${styles.sidebar} ${isOpen ? styles.open : ""}`} aria-label="Chat history">
            {/* Sidebar header */}
            <div className={styles.sidebarHeader}>
                <span className={styles.sidebarTitle}>History</span>
                <button className={styles.newChatButton} onClick={handleNewConversation}>
                    <svg width="14" height="14" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    New chat
                </button>
            </div>

            <div className={styles.sidebarDivider} />

            {/* History list */}
            <div className={styles.sidebarContent} ref={scrollContainerRef}>
                {Object.entries(groupedHistory).map(([group, items]) => (
                    <div key={group} className={styles.group}>
                        <p className={styles.groupLabel}>{t(group)}</p>
                        {items.map(item => (
                            <HistoryItem key={item.id} item={item} onSelect={handleSelect} onDelete={handleDelete} />
                        ))}
                    </div>
                ))}
                {isLoading && <Spinner style={{ marginTop: "12px" }} />}
                {history.length === 0 && !isLoading && <p className={styles.emptyMessage}>{t("history.noHistory")}</p>}
                {hasMoreHistory && !isLoading && <InfiniteLoadingButton func={loadMoreHistory} root={scrollContainerRef.current} />}
            </div>
        </aside>
    );
};

function groupHistory(history: HistoryData[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(lastMonth.getDate() - 30);

    return history.reduce(
        (groups, item) => {
            const itemDate = new Date(item.timestamp);
            let group;

            if (itemDate >= today) {
                group = "history.today";
            } else if (itemDate >= yesterday) {
                group = "history.yesterday";
            } else if (itemDate >= lastWeek) {
                group = "history.last7days";
            } else if (itemDate >= lastMonth) {
                group = "history.last30days";
            } else {
                group = itemDate.toLocaleDateString(undefined, { year: "numeric", month: "long" });
            }

            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(item);
            return groups;
        },
        {} as Record<string, HistoryData[]>
    );
}

const InfiniteLoadingButton = ({ func, root }: { func: () => void; root: Element | null }) => {
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        func();
                    }
                });
            },
            {
                root: root,
                threshold: 0
            }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [func, root]);

    return <div ref={sentinelRef} style={{ height: "1px", width: "100%" }} />;
};
